import { Dialog } from '@headlessui/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../buttons/Button';
import { COMMON_TRANSLATE_KEYS } from '../../../../constants/translate-keys';
import SwitchButton from '../../../buttons/SwitchButton';
import Divider from '../../../ui/Divider';

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  onApply: (days: number[]) => void;
  currency: string;
  start: string;
  end: string;
  day: number;
  price?: number | string;
};

const ApplyPriceModal = ({
  open,
  setOpen,
  price,
  currency,
  start,
  end,
  onApply,
  day,
}: Props) => {
  const [mondayChecked, setMondayChecked] = useState(day === 1);
  const [tuesdayChecked, setTuesdayChecked] = useState(day === 2);
  const [wednesdayChecked, setWednesdayChecked] = useState(day === 3);
  const [thursdayChecked, setThursdayChecked] = useState(day === 4);
  const [fridayChecked, setFridayChecked] = useState(day === 5);
  const [saturdayChecked, setSaturdayChecked] = useState(day === 6);
  const [sundayChecked, setSundayChecked] = useState(day === 0);

  const { t } = useTranslation();

  const applyButtonIsDisabled = useMemo(() => {
    return (
      [
        mondayChecked,
        tuesdayChecked,
        wednesdayChecked,
        thursdayChecked,
        fridayChecked,
        saturdayChecked,
        sundayChecked,
      ].filter((x) => x).length === 0
    );
  }, [
    mondayChecked,
    tuesdayChecked,
    wednesdayChecked,
    thursdayChecked,
    fridayChecked,
    saturdayChecked,
    sundayChecked,
  ]);

  const days = useMemo(() => {
    let daysArr: number[] = [];

    if (mondayChecked) {
      daysArr.push(1);
    }
    if (tuesdayChecked) {
      daysArr.push(2);
    }
    if (wednesdayChecked) {
      daysArr.push(3);
    }
    if (thursdayChecked) {
      daysArr.push(4);
    }
    if (fridayChecked) {
      daysArr.push(5);
    }
    if (saturdayChecked) {
      daysArr.push(6);
    }
    if (sundayChecked) {
      daysArr.push(0);
    }
    return daysArr;
  }, [
    mondayChecked,
    tuesdayChecked,
    wednesdayChecked,
    thursdayChecked,
    fridayChecked,
    saturdayChecked,
    sundayChecked,
  ]);

  const closeModal = useCallback(() => {
    setMondayChecked(day === 1);
    setTuesdayChecked(day === 2);
    setWednesdayChecked(day === 3);
    setThursdayChecked(day === 4);
    setFridayChecked(day === 5);
    setSaturdayChecked(day === 6);
    setSundayChecked(day === 0);
    setOpen(false);
  }, [setOpen, day]);

  if (!price) {
    return <></>;
  }

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      className="fixed inset-0 flex items-center justify-center z-[50]"
    >
      <Dialog.Overlay className="fixed inset-0 bg-black  opacity-50" />
      <div className="bg-gray-900 lg:w-[55%] lg-max-h-[60%] max-h-[90%] w-[80%] p-6 rounded-md absolute top-212 left-468 overflow-auto">
        <Dialog.Title>
          <div className="flex flex-col">
            <div className="flex w-full justify-end text-mainWhite">
              <button onClick={closeModal}>X</button>
            </div>
            <h1 className="flex flex-row justify-start text-white text-xl font-bold mb-6 text-center px-3">
              {`${t(COMMON_TRANSLATE_KEYS.APPLY_PRICE_MODAL_TITLE, {
                price: `${price} ${currency}`,
                start,
                end,
              })}: `}
            </h1>
          </div>
        </Dialog.Title>

        <Divider mb={3} />

        <Dialog.Description>
          <div className="flex flex-col w-full gap-3">
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={mondayChecked}
              onClick={() => setMondayChecked(!mondayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.MONDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={tuesdayChecked}
              onClick={() => setTuesdayChecked(!tuesdayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.TUESDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={wednesdayChecked}
              onClick={() => setWednesdayChecked(!wednesdayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.WEDNESDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={thursdayChecked}
              onClick={() => setThursdayChecked(!thursdayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.THURSDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={fridayChecked}
              onClick={() => setFridayChecked(!fridayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.FRIDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={saturdayChecked}
              onClick={() => setSaturdayChecked(!saturdayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.SATURDAY_NORMAL)}
            </SwitchButton>
            <SwitchButton
              style={{
                backgroundColor: 'transparent',
              }}
              isSelected={sundayChecked}
              onClick={() => setSundayChecked(!sundayChecked)}
            >
              {t(COMMON_TRANSLATE_KEYS.SUNDAY_NORMAL)}
            </SwitchButton>
          </div>
        </Dialog.Description>

        <Divider mb={3} mt={3} />

        <div className="flex flex-row gap-8 justify-around mt-5">
          <Button
            onClick={() => {
              onApply(days);
              closeModal();
            }}
            type="button"
            backgroundColor="bg-functionalGreen"
            textColor="text-white"
            width={16}
            height={3}
            isDisabled={applyButtonIsDisabled}
          >
            {t(COMMON_TRANSLATE_KEYS.APPLY)}
          </Button>
          <Button
            onClick={() => closeModal()}
            type="button"
            backgroundColor="bg-transparent"
            border="border"
            borderColor="border-mainBlue"
            textColor="text-mainBlue"
            width={16}
            height={3}
          >
            {t(COMMON_TRANSLATE_KEYS.CANCEL)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ApplyPriceModal;
